/**
 * Workspace › Deleted projects (ledger 18a): the tenant's soft-deleted projects
 * with Restore (POST /projects/{id}/restore, gated server-side by RestorableBy).
 * Route-guarded by RestrictedRedirect: GET /projects/deleted is not
 * restriction-scoped, so restricted users must not reach this list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDeletedProjects, restoreProject, type Project } from '../../packages/api-client/pm-api';
import { getErrorMessage } from '../../packages/api-client';
import { PageHeader, StateView, Table } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';

const status = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

export function DeletedProjectsPage() {
    usePageTitle('Deleted projects');
    const [projects, setProjects] = useState<Project[] | null>(null);
    const [loadError, setLoadError] = useState<{ message: string; denied: boolean } | null>(null);
    const [rowError, setRowError] = useState('');
    const [restored, setRestored] = useState<Project | null>(null);
    const [pending, setPending] = useState<string | null>(null);

    // ponytail: first 50 only; add paging when a workspace routinely has more.
    const load = useCallback(() => {
        setLoadError(null); setProjects(null);
        getDeletedProjects()
            .then(r => setProjects(r.data ?? []))
            .catch(e => setLoadError({ message: getErrorMessage(e), denied: status(e) === 403 }));
    }, []);
    useEffect(load, [load]);

    async function restore(p: Project) {
        setPending(p.id); setRowError(''); setRestored(null);
        try {
            await restoreProject(p.id);
            setProjects(prev => (prev ?? []).filter(x => x.id !== p.id));
            setRestored(p);
        } catch (err) {
            setRowError(`Couldn't restore "${p.name}": ${getErrorMessage(err)}`);
        } finally {
            setPending(null);
        }
    }

    return (
        <div>
            <PageHeader breadcrumb={[{ label: 'Projects', to: '/projects' }, { label: 'Deleted projects' }]}
                title="Deleted projects"
                subtitle="Projects deleted in this workspace. Restoring one brings back the project only: its tasks stay deleted and are restored separately from its Project tools › Data & lifecycle. Milestones deleted with a project can't be restored from the app." />
            {restored && (
                <p role="status">
                    Restored <Link to={`/projects/${restored.id}`}>{restored.name}</Link>. It is back in <Link to="/projects">Projects</Link>.
                    Its tasks are still deleted: <Link to={`/projects/${restored.id}?tab=Tools&view=data`}>restore its tasks</Link>.
                </p>
            )}
            {rowError && <p role="alert" className="form-error">{rowError}</p>}
            {loadError ? (loadError.denied
                ? <StateView state="denied" reason="Listing deleted projects needs permission to read projects." nextStep="Ask a workspace admin." />
                : <StateView state="error" title="Deleted projects couldn't be loaded" message={loadError.message} onRetry={load} />)
                : projects === null ? <StateView state="loading" label="Loading deleted projects" />
                : projects.length === 0 ? <StateView state="empty" title="No deleted projects" message="Deleted projects appear here and can be restored." />
                : (
                    <Table caption="Deleted projects" hideCaption rowKey={r => r.id as string}
                        data={projects as unknown as Record<string, unknown>[]}
                        columns={[
                            { key: 'name', header: 'Project', rowHeader: true },
                            { key: 'deleted_at', header: 'Deleted', render: r => (r.deleted_at ? new Date(r.deleted_at as string).toLocaleString() : '—') },
                            { key: 'restore', header: 'Action', render: r => {
                                const p = r as unknown as Project;
                                return <button type="button" className="btn btn-secondary btn-sm" aria-label={`Restore ${p.name}`}
                                    onClick={() => restore(p)} disabled={pending === p.id}>Restore</button>;
                            } },
                        ]} />
                )}
        </div>
    );
}
